import React, { useState, useEffect } from 'react';
import { testimonialsAPI } from '../../utils/api';
import Card from '../../components/common/Card';
import Button from '../../components/common/Button';
import Loading from '../../components/common/Loading';
import ErrorMessage from '../../components/common/ErrorMessage';
import Modal from '../../components/common/Modal';
import TestimonialForm from '../../components/admin/TestimonialForm';
import { FaEdit, FaTrash, FaPlus, FaStar } from 'react-icons/fa';

const TestimonialManager = () => {
  const [testimonials, setTestimonials] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [currentTestimonial, setCurrentTestimonial] = useState(null);
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    fetchTestimonials();
  }, []);

  const fetchTestimonials = async () => {
    try {
      setLoading(true);
      const response = await testimonialsAPI.getAll();
      setTestimonials(response.data.data || []);
    } catch (err) {
      setError(err.message || 'Failed to load testimonials');
    } finally {
      setLoading(false);
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Are you sure you want to delete this testimonial?')) {
      return;
    }

    try {
      await testimonialsAPI.delete(id);
      setTestimonials(testimonials.filter((t) => t._id !== id));
    } catch (err) {
      alert(err.message || 'Failed to delete testimonial');
    }
  };

  const handleOpenModal = (testimonial = null) => {
    setCurrentTestimonial(testimonial);
    setIsModalOpen(true);
  };

  const handleCloseModal = () => {
    setIsModalOpen(false);
    setCurrentTestimonial(null);
  };

  const handleSubmit = async (formData) => {
    try {
      setSubmitting(true);
      if (currentTestimonial) {
        // Update existing testimonial
        const response = await testimonialsAPI.update(
          currentTestimonial._id,
          formData
        );
        setTestimonials(
          testimonials.map((t) =>
            t._id === currentTestimonial._id ? response.data.data : t
          )
        );
      } else {
        // Create new testimonial
        const response = await testimonialsAPI.create(formData);
        setTestimonials([response.data.data, ...testimonials]);
      }
      handleCloseModal();
    } catch (err) {
      alert(err.message || 'Failed to save testimonial');
    } finally {
      setSubmitting(false);
    }
  };

  if (loading) {
    return <Loading text="Loading testimonials..." />;
  }

  return (
    <div className="max-w-7xl mx-auto">
      {/* Header */}
      <div className="flex justify-between items-center mb-6">
        <div>
          <h1 className="text-3xl font-heading font-bold text-secondary mb-2">
            Manage Testimonials
          </h1>
          <p className="text-text-primary font-body">
            Add and manage client testimonials
          </p>
        </div>
        <Button variant="primary" onClick={() => handleOpenModal()}>
          <FaPlus className="inline mr-2" />
          New Testimonial
        </Button>
      </div>

      {/* Error Message */}
      {error && <ErrorMessage message={error} className="mb-6" />}

      {/* Testimonials List */}
      {testimonials.length === 0 ? (
        <Card className="p-8 text-center">
          <p className="text-text-primary font-body">
            No testimonials yet. Add your first testimonial!
          </p>
        </Card>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          {testimonials.map((testimonial) => (
            <Card key={testimonial._id} className="p-6">
              <div className="flex items-start justify-between mb-4">
                <div className="flex items-center gap-2">
                  <FaStar className="text-yellow-500" />
                  <span className="text-sm font-body text-gray-600 capitalize">
                    {testimonial.relationship}
                  </span>
                  {testimonial.featured && (
                    <span className="bg-accent text-white px-2 py-1 rounded text-xs">
                      Featured
                    </span>
                  )}
                </div>
                <div className="flex space-x-2">
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => handleOpenModal(testimonial)}
                  >
                    <FaEdit />
                  </Button>
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => handleDelete(testimonial._id)}
                    className="text-red-600 hover:bg-red-50"
                  >
                    <FaTrash />
                  </Button>
                </div>
              </div>

              <blockquote className="text-text-primary font-body italic mb-4">
                "{testimonial.text}"
              </blockquote>

              <p className="text-sm font-body font-semibold text-secondary">
                — {testimonial.author}
              </p>
            </Card>
          ))}
        </div>
      )}

      {/* Modal for Create/Edit */}
      <Modal
        isOpen={isModalOpen}
        onClose={handleCloseModal}
        title={
          currentTestimonial ? 'Edit Testimonial' : 'Create New Testimonial'
        }
        size="large"
      >
        <TestimonialForm
          testimonial={currentTestimonial}
          onSubmit={handleSubmit}
          onCancel={handleCloseModal}
          loading={submitting}
        />
      </Modal>
    </div>
  );
};

export default TestimonialManager;
